import { defineStore } from 'pinia'
import * as cardApi from '../services/api/card'
import * as todoApi from '../services/api/todo'
import * as commentApi from '../services/api/comment'

export interface ICardLabelData {
  id: number
  color: string
  name: string
}
export interface IChecklistData {
  id: number
  todo: string
  is_completed: boolean
  card_id: number
  is_edit: boolean
}
export interface IActionData {
  id: number
  type: string
  body: string
  card_id: number
  user_id: number
  user: any
  created_at: string
}
export interface ICardData {
  id: number
  created_at: string
  updated_at: string
  title: string
  comment_count: number
  card_label_ids: Array<number>
  car_id: number
  type_id: number
  type: string
  creator_id: number
  assignee_id: number
  license_plate: string
  status: string
  body: string
  scheduled_at: string
  assignee: any
  card_labels: Array<ICardLabelData>
  checklist: Array<IChecklistData>
  images: Array<any>
  activities: Array<IActionData>
}

export const useCardStore = defineStore('card', {
  state: () => ({
    card: {
      id: 0,
      created_at: '',
      updated_at: '',
      title: '',
      comment_count: 0,
      card_label_ids: [],
      car_id: 0,
      type_id: 0,
      creator_id: 0,
      assignee_id: 0,
      license_plate: '',
      status: '',
      body: '',
      scheduled_at: '',
      assignee: {},
      card_labels: [],
      checklist: [],
      images: [],
      activities: [],
      type: '',
    } as ICardData,
    isLoading: false,
  }),

  actions: {
    setLoading(value: boolean) {
      this.isLoading = value
    },

    setCard(data: ICardData) {
      this.card = data
    },

    deleteTodo(data: number) {
      this.card.checklist = this.card.checklist.filter((c: any) => c.id !== data)
    },

    addTodo(data: any) {
      data.is_edit = false
      this.card.checklist.push(data)
    },

    addComment(data: any) {
      this.card.activities.unshift(data)
    },

    completeCard() {
      this.card.status = 'completed'
    },

    async getCard(card_id: number) {
      this.setLoading(true)
      try {
        const { data } = await cardApi.get(card_id)
        data.checklist = data.checklist.sort((a: any, b: any) =>
          new Date(a.created_at).getTime() > new Date(b.created_at).getTime() ? 1 : -1,
        )
        data.checklist = data.checklist.map((c: any) => {
          c.is_edit = false
          return c
        })
        data.activities = data.activities.sort((a: any, b: any) =>
          new Date(a.created_at).getTime() > new Date(b.created_at).getTime() ? -1 : 1,
        )
        data.comment_count = data.activities.filter((a: any) => a.type === 'comment').length
        this.setCard(data)
      } catch (e) {
        throw new Error(String(e))
      } finally {
        this.setLoading(false)
      }
    },

    async updateCard() {
      try {
        const { data } = await cardApi.update(this.card.id, this.card)
        this.setCard(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async createTodo() {
      try {
        const { data } = await todoApi.create({
          card_id: this.card.id,
          todo: 'To do',
        })
        this.addTodo(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async destroyTodo(todoId: number) {
      try {
        await todoApi.destroy(todoId)
        this.deleteTodo(todoId)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async createComment(value: string) {
      try {
        const { data } = await commentApi.create({
          type: 'comment',
          body: value,
          card_id: this.card.id,
        })
        this.addComment(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async complete() {
      try {
        await cardApi.complete(this.card.id)
        this.completeCard()
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async reschedule(data: any) {
      try {
        await cardApi.reschedule(this.card.id, data)
      } catch (e) {
        throw new Error(String(e))
      }
    },
  },
})

export const useCardListStore = defineStore('cardList', {
  state: () => ({
    allCards: {
      now: [],
      remain: [],
      completed: [],
    },
    myCards: {
      now: [],
      remain: [],
    },
    assignCards: {
      now: [],
      remain: [],
    },
    completedCards: {
      completed: [],
    },
    cardLabels: [] as ICardLabelData[],
    count: 0,
    isLoading: false,
  }),

  actions: {
    setCardLabels(data: ICardLabelData[]) {
      this.cardLabels = data
    },

    setCount(data: number) {
      this.count = data
    },

    setAllCards(data: any) {
      this.allCards = data
    },

    setMyCards(data: any) {
      this.myCards = data
    },

    setAssignCards(data: any) {
      this.assignCards = data
    },

    setCompletedCards(data: any) {
      this.completedCards = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    updateCard(card: any) {
      let index: number = this.myCards.now.findIndex((a: any) => a.id === card.id)
      if (index > -1) {
        this.myCards.now[index] = card as never
        return
      }
      index = this.myCards.remain.findIndex((a: any) => a.id === card.id)
      if (index > -1) {
        this.myCards.remain[index] = card as never
        return
      }
      index = this.assignCards.now.findIndex((a: any) => a.id === card.id)
      if (index > -1) {
        this.assignCards.now[index] = card as never
        return
      }
      index = this.assignCards.remain.findIndex((a: any) => a.id === card.id)
      if (index > -1) {
        this.assignCards.remain[index] = card as never
        return
      }
      index = this.completedCards.completed.findIndex((a: any) => a.id === card.id)
      if (index > -1) {
        this.completedCards.completed[index] = card as never
        return
      }
    },

    async countMyCards(params: any) {
      try {
        const { data } = await cardApi.listMyCards(params)
        const count = data.now.filter((c: any) => c.status !== 'completed').length + data.remain.length
        this.setCount(count)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async createCard(payload: any) {
      try {
        await cardApi.create(payload)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async listCardLabels(params?: any) {
      try {
        const { data } = await cardApi.listCardLabels(params)
        this.setCardLabels(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async listMyCards(params: any) {
      try {
        this.setLoading(true)
        const { data } = await cardApi.listMyCards(params)
        this.setMyCards(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAssignCards(params: any) {
      try {
        this.setLoading(true)
        const { data } = await cardApi.listAssignCards(params)
        this.setAssignCards(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listCompletedCards(params: any) {
      try {
        this.setLoading(true)
        const { data } = await cardApi.listCompletedCards(params)
        this.setCompletedCards(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllCards(params: any) {
      try {
        this.setLoading(true)
        const { data } = await cardApi.listAllCards(params)
        this.setAllCards(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async toggleTodoCompletion(data: any) {
      try {
        if (data.todo.is_completed) {
          await todoApi.complete(data.todo.id)
        } else {
          await todoApi.unComplete(data.todo.id)
        }
        this.updateCard(data.card)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async updateTodo(data: any) {
      try {
        await todoApi.update(data.id, data)
      } catch (e) {
        throw new Error(String(e))
      }
    },
  },
})
